/**
 * Country API Client - Frontend API calls for country operations
 */

import type { Country, CountryWithUniversities, CountryFilters, CountryFilterOptions, CountryBrowseResult } from '../types';

const API_BASE = '/api/country';

export const countryAPI = {
  /**
   * Browse countries with filtering
   */
  async browseCountries(filters: CountryFilters = {}): Promise<CountryBrowseResult> {
    const params = new URLSearchParams();

    if (filters.region) params.append('region', filters.region);
    if (filters.minSafetyIndex !== undefined) params.append('min_safety', filters.minSafetyIndex.toString());
    if (filters.maxLivingCost !== undefined) params.append('max_living_cost', filters.maxLivingCost.toString());
    if (filters.postStudyWork) params.append('post_study_work', 'true');
    if (filters.partTimeWork) params.append('part_time_work', 'true');
    if (filters.permanentResidency) params.append('permanent_residency', 'true');
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sort) params.append('sort', filters.sort);

    const response = await fetch(`${API_BASE}/browse?${params.toString()}`);
    if (!response.ok) throw new Error('Failed to browse countries');
    return response.json();
  },

  /**
   * Get filter options
   */
  async getFilterOptions(): Promise<CountryFilterOptions> {
    const response = await fetch(`${API_BASE}/filters`);
    if (!response.ok) throw new Error('Failed to fetch filter options');
    return response.json();
  },

  /**
   * Get country by ID
   */
  async getCountryById(id: number): Promise<CountryWithUniversities> {
    const response = await fetch(`${API_BASE}/${id}`);
    if (!response.ok) throw new Error('Failed to fetch country');
    return response.json();
  },

  /**
   * Get country by code
   */
  async getCountryByCode(code: string): Promise<CountryWithUniversities> {
    const response = await fetch(`${API_BASE}/code/${code}`);
    if (!response.ok) throw new Error('Failed to fetch country');
    return response.json();
  },

  /**
   * Search countries
   */
  async searchCountries(query: string, limit: number = 10): Promise<Country[]> {
    const params = new URLSearchParams({ q: query, limit: limit.toString() });
    const response = await fetch(`${API_BASE}/search?${params.toString()}`);
    if (!response.ok) throw new Error('Failed to search countries');
    return response.json();
  },

  /**
   * Get popular countries
   */
  async getPopularCountries(limit: number = 10): Promise<Country[]> {
    const params = new URLSearchParams({ limit: limit.toString() });
    const response = await fetch(`${API_BASE}/popular?${params.toString()}`);
    if (!response.ok) throw new Error('Failed to fetch popular countries');
    return response.json();
  }
};
